import { Component, type ReactNode } from "react";
import { isChunkLoadError, reloadOnceForChunkError } from "../lib/chunk-reload";

interface State {
  readonly failed: boolean;
}

/**
 * OWN-117: last-resort boundary above the whole app. A failed lazy import (new deploy, old tab)
 * reloads once; any other crash shows a calm bilingual page with a reload button instead of a
 * blank screen. It sits above the i18n provider, so its text is static AR + EN.
 */
export class RootErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) reloadOnceForChunkError();
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main
        role="alert"
        className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-bg p-6 text-center text-fg"
      >
        <h1 className="text-xl font-semibold">حدث خطأ غير متوقع</h1>
        <p className="text-sm text-fg-muted">أعد تحميل الصفحة للمتابعة.</p>
        <p dir="ltr" className="text-sm text-fg-muted">
          Something went wrong. Reload the page to continue.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-action-primary px-4 py-2 text-sm font-medium text-on-action-primary"
        >
          إعادة التحميل / Reload
        </button>
      </main>
    );
  }
}
